import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api';

export type AssetType =
  | 'llms_txt'
  | 'llms_full_txt'
  | 'mcp_server'
  | 'cursor_rules'
  | 'copilot_instructions'
  | 'agent_skills';

export interface AssetSummary {
  type: AssetType;
  path: string;
  version: number;
  generated_at: string;
  generated_from_audit_id: string | null;
  has_content: boolean;
  preview: string | null;
}

export interface AssetListResponse {
  items: AssetSummary[];
  enabled_types: AssetType[];
  hosted_base_url: string;
  dev_base_url: string;
}

export interface AssetOut {
  id: string;
  type: AssetType;
  path: string;
  content: string | null;
  version: number;
  generated_at: string;
  generated_from_audit_id: string | null;
}

export function useAssets() {
  return useQuery({
    queryKey: ['assets'],
    queryFn: () => api<AssetListResponse>('/api/assets'),
    staleTime: 10_000,
  });
}

export function useAssetDetail(type: AssetType | undefined) {
  return useQuery({
    queryKey: ['asset', type],
    queryFn: () => api<AssetOut>(`/api/assets/${type}`),
    enabled: Boolean(type),
  });
}

export function useRegenerateAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (type: AssetType) =>
      api<{ regenerated: string[] }>(`/api/assets/${type}/regenerate`, { method: 'POST' }),
    onSuccess: (_data, type) => {
      qc.invalidateQueries({ queryKey: ['assets'] });
      qc.invalidateQueries({ queryKey: ['asset', type] });
    },
  });
}

export function useRegenerateAllAssets() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<{ regenerated: string[] }>('/api/assets/regenerate-all', { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assets'] });
    },
  });
}
